import React, { useEffect, useRef, useState } from 'react';
import './PlatformSection.css';
import { gsap } from 'gsap';
import useScrollHijack from '../../hooks/useScrollHijack';

// Reordered to match the requirement sequence
const platformTexts = [
  { key: 'people', label: 'people over business', className: 'text-people' },
  { key: 'commitment', label: 'commitment to portfolio', className: 'text-commitment' },
  { key: 'empathy', label: 'empathy', className: 'text-empathy' },
  { key: 'roi', label: 'roi on founder interaction', className: 'text-roi' },
  { key: 'tech', label: 'tech and data', className: 'text-tech' },
  { key: 'methodical', label: 'medical risk underwriting', className: 'text-methodical' },
];

const centerText = 'we are yet another seed fund !';

// Desktop triangle positions - adjusted to match the new order
const trianglePositions = [
  { top: '65%', left: '25%' }, // people
  { top: '30%', left: '50%' }, // commitment
  { top: '15%', left: '25%' }, // empathy
  { top: '80%', left: '55%' }, // roi
  { top: '65%', left: '75%' }, // tech
  { top: '0%', left: '80%' }, // methodical
];

// Mobile triangle positions - adjusted to match the new order
const mobileTrianglePositions = [
  { top: '10%', left: '49%', transform: 'translateX(-50%) translateY(-50%)' }, // people (top)
  { top: '25%', left: '49%', transform: 'translateX(-50%) translateY(-50%)' }, // commitment
  { top: '40%', left: '49%', transform: 'translateX(-50%) translateY(-50%)' }, // empathy
  { top: '55%', left: '49%', transform: 'translateX(-50%) translateY(-50%)' }, // roi
  { top: '70%', left: '49%', transform: 'translateX(-50%) translateY(-50%)' }, // tech
  { top: '85%', left: '49%', transform: 'translateX(-50%) translateY(-50%)' }, // methodical (bottom)
];

const PlatformSection = () => {
  const sectionRef = useRef(null);
  const triangleRef = useRef(null);
  const contentRef = useRef(null);
  const scrollIndicatorRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const isDarkMode = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';

  // Check if screen is mobile (768px or less)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Handle step change (triangle animation)
  const handleStepChange = (index, direction, isExiting) => {
    if (isExiting) {
      // Handle exit animation
      animateTriangleExit(direction);
    } else {
      // Normal step change
      animateToPosition(index);
      updateScrollIndicator(index, direction);
    }
  };
  
  // Animate triangle exit
  const animateTriangleExit = (direction) => {
    if (!triangleRef.current) return;
    
    // Create a timeline for coordinated exit animations
    const tl = gsap.timeline({
      defaults: { 
        ease: "power3.inOut",
        duration: 0.5
      }
    });
    
    // Add a spin effect to the triangle as it exits
    const rotations = direction === 'down' ? 180 : -180;
    
    // Animate triangle to center with rotation and fade out
    tl.to(triangleRef.current, {
      top: '50%',
      left: '50%',
      scale: direction === 'down' ? 0.2 : 1.8,
      opacity: 0,
      rotation: rotations,
      duration: 0.8,
      ease: "power3.inOut"
    }, 0);
    
    // Fade out all texts with staggered timing
    const textElements = document.querySelectorAll('.platform-text');
    tl.to(textElements, {
      opacity: 0,
      scale: direction === 'down' ? 0.9 : 1.1,
      duration: 0.5,
      stagger: 0.03, // Staggered animation for a wave effect
      ease: "power2.out"
    }, 0);
    
    // Fade in center text with a slight pulse
    const centerTextEl = document.querySelector('.platform-center-text');
    if (centerTextEl) {
      tl.to(centerTextEl, {
        opacity: 1,
        scale: 1.2,
        duration: 0.4,
        ease: "power2.out"
      }, 0.2).to(centerTextEl, {
        scale: 1.1,
        duration: 0.4,
        ease: "elastic.out(1, 0.5)"
      }, 0.6);
    }
    
    // Add a small delay before allowing scroll to continue
    // This ensures the animation is visible before scrolling continues
    setTimeout(() => {
      // This will be handled by the hook's exit function
    }, 300);
  };

  // Handle entering the section
  const handleEnterSection = (direction) => {
    // Show scroll indicator with animation
    if (scrollIndicatorRef.current) {
      gsap.fromTo(scrollIndicatorRef.current, 
        { opacity: 0, y: 20 }, 
        { opacity: 1, y: 0, duration: 0.5, delay: 0.3 }
      );
    }
    
    // If entering from top, animate triangle from off-screen top
    if (direction === 'down' && triangleRef.current) {
      const positions = isMobile ? mobileTrianglePositions : trianglePositions;
      const firstPosition = positions[0];
      
      // Create a timeline for entrance animation
      const tl = gsap.timeline({
        defaults: { 
          ease: "elastic.out(1, 0.5)",
          duration: 1
        }
      });
      
      // First set it off-screen
      gsap.set(triangleRef.current, {
        top: '-50px',
        left: firstPosition.left,
        opacity: 0,
        scale: 0.5,
        rotation: -30 // Start with a slight rotation
      });
      
      // Then animate to first position with a bounce effect
      tl.to(triangleRef.current, {
        top: firstPosition.top,
        opacity: 1,
        scale: 1.2, // Slightly overshoot
        rotation: 10, // Rotate slightly in the other direction
        duration: 0.8,
        ease: "power3.out",
        delay: 0.2
      }).to(triangleRef.current, {
        scale: 1,
        rotation: 0, // Return to normal rotation
        duration: 0.5,
        ease: "elastic.out(1, 0.5)"
      });
      
      // Highlight the first text with a slight delay
      const firstTextEl = document.querySelector(`.${platformTexts[0].className}`);
      if (firstTextEl) {
        tl.to(firstTextEl, {
          opacity: 1,
          fontWeight: 700,
          scale: 1.05,
          duration: 0.5,
          delay: 0.3,
          ease: "back.out(1.7)"
        }, 0.3);
      }
    }
    // If entering from bottom, animate triangle from off-screen bottom
    else if (direction === 'up' && triangleRef.current) {
      const positions = isMobile ? mobileTrianglePositions : trianglePositions;
      const lastPosition = positions[positions.length - 1];
      
      // Create a timeline for entrance animation
      const tl = gsap.timeline({
        defaults: { 
          ease: "elastic.out(1, 0.5)",
          duration: 1
        }
      });
      
      // First set it off-screen
      gsap.set(triangleRef.current, {
        top: '150%',
        left: lastPosition.left,
        opacity: 0,
        scale: 0.5,
        rotation: 30 // Start with a slight rotation
      });
      
      // Then animate to last position with a bounce effect
      tl.to(triangleRef.current, {
        top: lastPosition.top,
        opacity: 1,
        scale: 1.2, // Slightly overshoot
        rotation: -10, // Rotate slightly in the other direction
        duration: 0.8,
        ease: "power3.out",
        delay: 0.2
      }).to(triangleRef.current, {
        scale: 1,
        rotation: 0, // Return to normal rotation
        duration: 0.5,
        ease: "elastic.out(1, 0.5)"
      });
      
      // Highlight the last text with a slight delay
      const lastTextEl = document.querySelector(`.${platformTexts[platformTexts.length - 1].className}`);
      if (lastTextEl) {
        tl.to(lastTextEl, {
          opacity: 1,
          fontWeight: 700,
          scale: 1.05,
          duration: 0.5,
          delay: 0.3,
          ease: "back.out(1.7)"
        }, 0.3);
      }
    }
  };

  // Handle exiting the section
  const handleExitSection = (direction) => {
    // Hide scroll indicator
    if (scrollIndicatorRef.current) {
      gsap.to(scrollIndicatorRef.current, { opacity: 0, duration: 0.3 });
    }
    
    // Make sure triangle is hidden when exiting
    if (triangleRef.current) {
      gsap.to(triangleRef.current, { 
        opacity: 0, 
        duration: 0.3,
        scale: direction === 'down' ? 0.5 : 1.5
      });
    }
    
    // Reset all text opacities
    const textElements = document.querySelectorAll('.platform-text');
    textElements.forEach((el) => {
      gsap.to(el, {
        opacity: 0.25,
        duration: 0.3
      });
    });
    
    // Reset center text
    const centerTextEl = document.querySelector('.platform-center-text');
    if (centerTextEl) {
      gsap.to(centerTextEl, {
        opacity: 0.25,
        scale: 1,
        duration: 0.3
      });
    }
  };

  // Use our custom scroll hijack hook
  const { 
    currentStep, 
    isInSection, 
    scrollDirection,
    setIsAnimating
  } = useScrollHijack({
    sectionRef,
    totalSteps: platformTexts.length,
    onStepChange: handleStepChange,
    onEnterSection: handleEnterSection,
    onExitSection: handleExitSection
  });

  // Update scroll indicator based on current position
  const updateScrollIndicator = (index, direction) => {
    if (!scrollIndicatorRef.current) return;
    
    const isFirst = index === 0;
    const isLast = index === platformTexts.length - 1;
    
    // Update indicator text based on position
    let indicatorText = "Scroll to explore";
    
    if (isLast && direction === 'down') {
      indicatorText = "Scroll down to continue";
    } else if (isFirst && direction === 'up') {
      indicatorText = "Scroll up to continue";
    }
    
    // Animate indicator text change
    gsap.to(scrollIndicatorRef.current.querySelector('.scroll-indicator-text'), {
      opacity: 0,
      y: -10,
      duration: 0.2,
      onComplete: () => {
        scrollIndicatorRef.current.querySelector('.scroll-indicator-text').textContent = indicatorText;
        gsap.to(scrollIndicatorRef.current.querySelector('.scroll-indicator-text'), {
          opacity: 1,
          y: 0,
          duration: 0.2
        });
      }
    });
  };

  // Animate triangle to the next position
  const animateToPosition = (index) => {
    if (!triangleRef.current) {
      setIsAnimating(false);
      return;
    }

    const positions = isMobile ? mobileTrianglePositions : trianglePositions;
    const position = positions[index];
    
    // Reset triangle opacity if it was previously faded out
    gsap.to(triangleRef.current, {
      opacity: 1,
      scale: 1,
      duration: 0.5,
      ease: "power2.out"
    });
    
    // Create a timeline for ultra-smooth, slow coordinated animations
    const tl = gsap.timeline({
      defaults: { 
        ease: "power2.out", // Ultra-smooth easing
        duration: 1.4 // Slower for maximum smoothness
      },
      onComplete: () => {
        setIsAnimating(false);
      }
    });
    
    // Ultra-smooth triangle movement - no rotation, no interruptions
    tl.to(triangleRef.current, {
      top: position.top,
      left: position.left,
      transform: position.transform || 'translate(-50%, -50%)',
      duration: 1.4, // Very slow and smooth movement
      ease: "power2.out", // Smooth deceleration
    }, 0);
    
    // Minimal scale effect for subtle life
    tl.to(triangleRef.current, {
      scale: 1.008, // Very subtle scale
      duration: 0.7,
      ease: "power2.out"
    }, 0).to(triangleRef.current, {
      scale: 1,
      duration: 0.7,
      ease: "power2.out"
    }, 0.7);
    
    // Smooth text animations
    const textElements = document.querySelectorAll('.platform-text');
    textElements.forEach((el, i) => {
      const originalIndex = isMobile ? platformTexts.length - 1 - i : i;
      
      if (originalIndex === index) {
        // Activate this text smoothly and slowly
        tl.to(el, {
          opacity: 1,
          fontWeight: 700,
          scale: 1.008, // Very subtle scale
          duration: 0.9,
          ease: "power2.out"
        }, 0.25);
      } else {
        // Deactivate other texts smoothly
        tl.to(el, {
          opacity: 0.25,
          fontWeight: 400,
          scale: 1,
          duration: 0.7,
          ease: "power2.out"
        }, 0);
      }
    });
    
    // Dim the center text when a platform text is active
    const centerTextEl = document.querySelector('.platform-center-text');
    if (centerTextEl) {
      tl.to(centerTextEl, {
        opacity: 0.25,
        scale: 1,
        duration: 0.7,
        ease: "power2.out"
      }, 0);
    }
  };

  // For mobile, we want to display texts in reverse order (bottom to top)
  const mobileTexts = [...platformTexts].reverse();
  const displayTexts = isMobile ? mobileTexts : platformTexts;

  return (
    <section className="platform-section" ref={sectionRef} id="platform-section">
      <div className="platform-content" ref={contentRef}>
        <div className="platform-center-text">{centerText}</div>
        
        {displayTexts.map((item, idx) => {
          const originalIndex = isMobile ? platformTexts.length - 1 - idx : idx;
          const isActive = currentStep === originalIndex;
          
          return (
            <div
              key={item.key}
              className={`platform-text ${item.className} ${isActive ? 'active' : ''}`}
            >
              {item.label}
            </div>
          );
        })}
        
        {/* Animated triangle - always render but control visibility with opacity */}
        <svg
          ref={triangleRef}
          className="platform-triangle"
          width="250"
          height="250"
          viewBox="0 0 200 200"
          style={{
            opacity: currentStep === -1 ? 0 : 1,
            ...(currentStep >= 0 && currentStep < platformTexts.length ? 
              (isMobile ? mobileTrianglePositions[currentStep] : trianglePositions[currentStep]) : 
              { top: '50%', left: '50%' })
          }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M 20 100 L 150 40 Q 120 100 150 160 Z" fill="#0191c6" />
        </svg>
        
        {/* Scroll indicator */}
        <div className="scroll-indicator" ref={scrollIndicatorRef} style={{ opacity: isInSection ? 1 : 0 }}>
          <div className="scroll-indicator-text">Scroll to explore</div>
          <div className="scroll-indicator-arrow"></div>
        </div>
      </div>
    </section>
  );
};

export default PlatformSection;